import { Pagination, Popconfirm, message, Space, Popover, Table } from 'antd';
import {
  CheckOutlined,
  CloseCircleOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import ReactSelect from 'react-select';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { ColumnsType } from 'antd/lib/table';

import useTranslation from 'customHooks/translate';
import { useDialogHandler } from 'hooks/useDialogHandler';
import AddNewInvoiceForm from 'pages/Dialogs/AddNewInvoiceForm';
import {
  getCostDetails,
  getCostLinesDetails,
  getInvoicePaymentDetails,
  cancelCost,
  deleteInvoicePayment,
  updateCost,
} from 'services/invoices';
import { getManagers } from 'services/managers';
import { getJobs } from 'services/jobs';
import { setCulture } from 'services/others';
import { getAccountData } from 'services/accounts';
import { CostDetailsHeaderCustomError } from 'types';
import {
  ACCOUNT_INFO,
  COST_DETAILS,
  COST_LINES_DETAILS,
  GET_COST_PAYMENT_DETAILS,
  GET_INVOICE_PAYMENT_DETAILS,
  INVOICE_JOBS,
  MANAGERS,
  REPORTS_EXPENSES_SUMMARY,
} from 'events/types';
import StatusTag from 'pages/Invoices/StatusTag';
import { EXPENSES_PAGE_URL, PAGE_URL_KEY, REPORTS_PAGE_URL } from 'consts';

import PaymentModal from '../../../Dialogs/PaymentModal';
import CostDetailsInfo from './CostDetailsInfo';

export const CostDetailsHeader = () => {
  const queryClient = useQueryClient();
  const translation = useTranslation();
  const navigate = useNavigate();
  const { id: costId, partnerId } = useParams();

  const [openModal, setOpenModal] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem('language')
  );
  const [formKey, setFormKey] = useState(0);
  const [isLanguageChanging, setIsLanguageChanging] = useState(false);
  const [loadingCostUpdate, setLoadingCostUpdate] = useState(false);
  const [invoiceDialogState, openInvoiceDialog, closeInvoiceDialog] =
    useDialogHandler();
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  const previousPageUrl = localStorage.getItem(PAGE_URL_KEY);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { data: costDetails, refetch: refetchInvoiceDetails } = useQuery({
    queryKey: [COST_DETAILS, costId, currentLanguage],
    queryFn: () => getCostDetails(+costId!),
    select: (response) => response.data.data,
    enabled: !isLanguageChanging,
  });

  const { data: costPaymentDetails, refetch: refetchPaymentDetails } = useQuery(
    {
      queryKey: [GET_COST_PAYMENT_DETAILS, costId, pageSize, pageIndex],
      queryFn: () => getInvoicePaymentDetails(+costId!, pageSize, pageIndex),
      select: (response) => response.data,
      enabled: !isLanguageChanging,
    }
  );

  const { data: costLinesDetails } = useQuery({
    queryKey: [COST_LINES_DETAILS, costId],
    queryFn: () => getCostLinesDetails(+costId!),
    select: (response) => response.data.data,
  });

  const { data: managers } = useQuery({
    queryKey: [MANAGERS],
    queryFn: getManagers,
    select: (response) => {
      return response.data.data?.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  const { data: jobNumbers } = useQuery({
    queryKey: [INVOICE_JOBS, costId],
    queryFn: () => getJobs(costId),
    select: (response) => {
      return response.data.data?.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  useEffect(() => {
    const checkLanguageAndUpdateBackend = async () => {
      const storedLanguage = localStorage.getItem('language');
      if (storedLanguage && storedLanguage !== currentLanguage) {
        setIsLanguageChanging(true);

        try {
          await setCulture(storedLanguage);

          setCurrentLanguage(storedLanguage);

          await new Promise((resolve) => setTimeout(resolve, 300));

          await refetchInvoiceDetails();
          await refetchPaymentDetails();
          setFormKey((prev) => prev + 1);
        } catch {
        } finally {
          setIsLanguageChanging(false);
        }
      }
    };

    checkLanguageAndUpdateBackend();

    const handleStorageChange = () => {
      checkLanguageAndUpdateBackend();
    };

    window.addEventListener('storage', handleStorageChange);

    const intervalId = setInterval(checkLanguageAndUpdateBackend, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [currentLanguage, refetchInvoiceDetails, refetchPaymentDetails]);

  const cost = costDetails;

  const handleCancelInvoice = async (invoiceId) => {
    try {
      const response = await cancelCost(invoiceId);

      navigate('/invoices/expenses');
    } catch (error) {
      message.error('Error cancelling invoice. Please try again later.');
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    setPageIndex(page - 1);
    if (size) {
      setPageSize(size);
    }
  };

  const handleDeletePayment = async (paymentReceiveId) => {
    try {
      await deleteInvoicePayment(paymentReceiveId);
      message.success(translation.messages.successDeleted);
      refetchPaymentDetails();
      queryClient.invalidateQueries([
        GET_INVOICE_PAYMENT_DETAILS,
        costId,
        pageSize,
        pageIndex,
      ]);
      queryClient.invalidateQueries([COST_DETAILS, costId]);
      queryClient.invalidateQueries([REPORTS_EXPENSES_SUMMARY, partnerId]);
    } catch (error) {
      message.error('Error deleting payment. Please try again later.');
    }
  };

  const handleGoBack = () => {
    if (previousPageUrl === REPORTS_PAGE_URL) {
      navigate(-1);
    } else if (previousPageUrl === EXPENSES_PAGE_URL) {
      navigate('/invoices/expenses');
    } else navigate(-1);
  };

  const handleSubmit = (values) => {
    setLoadingCostUpdate(true);
    const payload = {
      invoiceId: costId,
      managerId: values?.managerId?.value,
      invoiceJobId: values?.invoiceJobId?.value,
      invoiceNumber: values?.invoiceNumber,
      invoiceDate: values?.invoiceDate,
      dueDate: values?.dueDate,
      contractNumber: values?.contractNumber || null,
      contractDate: values?.contractDate || null,
    };

    const method = updateCost;
    method(payload)
      .then(() => {
        message.success(`${translation.messages.successUpdated}`);
        queryClient.invalidateQueries({ queryKey: ['costDetails'] });
      })
      .catch((error: CostDetailsHeaderCustomError) => {
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
      })
      .finally(() => {
        setLoadingCostUpdate(false);
      });
  };

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
    refetchOnWindowFocus: true,
    staleTime: 0,
    cacheTime: 0,
  });

  const accountData = accountInfoQuery.data;
  const showPage = costDetails && managers && jobNumbers;

  const itemColumns: ColumnsType<any> = [
    {
      title: translation.InvoiceDetails.ItemName,
      dataIndex: 'description',
      key: 'description',
      width: 500,
      align: 'center',
      render: (text) => (
        <div
          style={{
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            padding: '20px',
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: translation.InvoiceDetails.SaleType,
      dataIndex: 'salesTypeDetail',
      key: 'salesTypeDetail',
      align: 'center',
      width: 200,
    },
    {
      title: translation.InvoiceDetails.Price,
      dataIndex: 'price',
      key: 'price',
      align: 'center',
      width: 200,
      render: (value, row) => `${value} ${row.currency}`,
    },
    {
      title: translation.InvoiceDetails.Quantity,
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
      width: 200,
    },
    {
      title: translation.InvoiceDetails.Unit,
      dataIndex: 'measurementUnitDetail',
      key: 'measurementUnitDetail',
      align: 'center',
      width: 200,
    },
    {
      title: translation.InvoiceDetails.Weight,
      dataIndex: 'weight',
      key: 'weight',
      align: 'center',
      width: 200,
      render: (value) => `${value} kq`,
    },
    {
      title: translation.InvoiceDetails.Discount,
      dataIndex: 'discountRate',
      key: 'discountRate',
      align: 'center',
      width: 200,
      render: (value) => `${value} %`,
    },
    {
      title: translation.InvoiceDetails.TaxType,
      dataIndex: 'taxTypeDetail',
      key: 'taxTypeDetail',
      align: 'center',
      width: 200,
    },
  ];

  const paymentColumns: ColumnsType<any> = [
    {
      title: translation.InvoiceDetails.PaymentDetails,
      dataIndex: 'paymentMethodDesc',
      key: 'paymentMethodDesc',
      align: 'center',
      width: 200,
    },
    {
      title: translation.InvoiceDetails.Total,
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      align: 'center',
      width: 150,
      render: (value, row) => `${value} ${row.currency}`,
    },
    {
      title: translation.InvoiceDetails.Tax,
      dataIndex: 'taxAmount',
      key: 'taxAmount',
      align: 'center',
      width: 150,
      render: (value, row) => `${value} ${row.currency}`,
    },
    {
      title: translation.InvoiceDetails.PaymentDate,
      dataIndex: 'paymentReceivedDate',
      key: 'paymentReceivedDate',
      align: 'center',
      width: 180,
      render: (value) => value?.slice(0, 10),
    },
    {
      title: translation.action.Action,
      key: 'action',
      align: 'center',
      width: 120,
      render: (_, item) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDeletePayment(item.paymentReceiveId)}
                  okText={translation.InvoiceDetails.Yes}
                  cancelText={translation.InvoiceDetails.No}
                >
                  <img
                    src="assets/delete-icon.svg"
                    alt="Delete"
                    style={{ cursor: 'pointer' }}
                  />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
                cursor: 'pointer',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle" style={{ paddingRight: '10px' }}>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() => handleDeletePayment(item.paymentReceiveId)}
              okText={translation.InvoiceDetails.Yes}
              cancelText={translation.InvoiceDetails.No}
            >
              <img
                src="assets/delete-icon.svg"
                alt="Delete"
                className="delete"
                style={{ cursor: 'pointer' }}
              />
            </Popconfirm>
          </Space>
        ),
    },
  ];

  const actionBtnBase =
    'flex w-full items-center justify-center gap-[6px] rounded-[6px] border px-[12px] py-[8px] text-[14px] font-medium leading-[16px] transition-all duration-300 md:w-max md:px-[24px]';
  const btnBlue = `${actionBtnBase} 
   bg-blue-50 border-blue-200 text-blue-700 
   hover:bg-blue-100`;
  const btnGreen = `${actionBtnBase} 
   bg-green-50 border-green-200 text-green-700 
   hover:bg-green-100`;
  const btnRed = `${actionBtnBase} 
   bg-red-50 border-red-200 text-red-700 
   hover:bg-red-100`;

  return (
    showPage && (
      <>
        <div className="flex-wrap items-center justify-between gap-[12px] border-b border-b-[#f5f7f9] px-[16px] py-[12px] md:flex">
          <div className="flex w-full flex-wrap items-center justify-between gap-[10px] md:w-auto md:justify-start md:gap-[24px]">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-[5px] rounded-[8px] bg-[#F6F6F6] px-[12px] py-[8px] text-[14px] font-medium text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[20px]"
            >
              <ArrowBackIosNewIcon style={{ width: '14px', height: '14px' }} />
              {translation.InvoiceDetails.Back}
            </button>
            <p className="text-[18px] font-semibold text-[#1D1D1D] md:text-[20px]">
              {cost?.invoiceNumber}
            </p>
            <StatusTag invoiceStatus={cost?.invoiceStatus} />
          </div>
          <div className="mt-[12px] grid grid-cols-2 flex-wrap items-center gap-[10px] md:mt-[unset] md:flex">
            {accountData?.countryCode &&
              accountData?.countryCode === 'AZ' &&
              accountData.defaultCurrencyId === 'AZN' && (
                <button onClick={openInvoiceDialog} className={btnBlue}>
                  <ProfileOutlined className="hidden md:block" />
                  <p>{translation.electronicInvoice.Header}</p>
                </button>
              )}
            <button onClick={handleOpenModal} className={btnGreen}>
              <CheckOutlined className="hidden md:block" />
              <p>{translation.InvoiceDetails.MakeCostPayment}</p>
            </button>
            <Popconfirm
              title={translation.InvoiceDetails.cancelInvoice}
              onConfirm={() => handleCancelInvoice(costId)}
              okText={
                <span style={{ color: 'black' }}>
                  {translation.InvoiceDetails.Yes}
                </span>
              }
              cancelText={
                <span style={{ color: 'black' }}>
                  {translation.InvoiceDetails.No}
                </span>
              }
            >
              <button className={btnRed}>
                <CloseCircleOutlined className="hidden md:block" />
                <p> {translation.InvoiceDetails.CancelInvoice}</p>
              </button>
            </Popconfirm>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
          <div className="col-span-2">
            <Formik
              key={formKey}
              enableReinitialize={true}
              initialValues={{
                customerName: cost.partnerName,
                countryName: cost.countryName,
                location: cost.partnerLocation,
                invoiceNumber: cost.invoiceNumber,
                managerId: managers?.find(
                  (manager) => manager?.value === cost?.managerId
                ),
                invoiceDate: cost.invoiceDate.slice(0, 10),
                dueDate: cost.dueDate.slice(0, 10),
                contractNumber: cost.contractNumber || '',
                contractDate: cost.contractDate
                  ? cost.contractDate.slice(0, 10)
                  : '',
                invoiceJobId: jobNumbers?.find(
                  (jobNumber) => jobNumber?.value === cost.invoiceJobId
                ),
              }}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
            >
              {({ setFieldValue, handleChange, values }) => (
                <Form>
                  <div className="flex grid-cols-3 flex-wrap items-center gap-[32px] border-b border-b-[#f5f7f9] px-[16px] py-[12px] md:grid md:py-[24px]">
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.CustomerName}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {cost.partnerName}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.CountryName}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {cost.countryName}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.InvoiceDetails.Location}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {cost.partnerLocation}
                      </p>
                    </div>
                  </div>
                  <div className="flex grid-cols-3 flex-wrap items-center gap-[32px] border-b border-b-[#f5f7f9] px-[16px] py-[12px] md:grid md:py-[24px]">
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.electronicInvoice.LabelText}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {cost.qaimeNo || translation.subusers.noData}
                      </p>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <p className="text-[16px] font-medium text-[#1D1D1D]">
                        {translation.electronicInvoice.LabelDateText}
                      </p>
                      <p className="text-[16px] font-normal text-[#1D1D1D]">
                        {cost.qaimeDate
                          ? cost.qaimeDate?.slice(0, 10)
                          : translation.subusers.noData}
                      </p>
                    </div>
                  </div>
                  <div className="grid items-end gap-[12px] border-b border-b-[#f5f7f9] px-[16px] py-[24px] md:grid-cols-2 md:gap-[32px] lg:grid-cols-3">
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.InvoiceDetails.InvoiceNumber}
                      </p>
                      <Input
                        type="text"
                        name="invoiceNumber"
                        onChange={handleChange}
                        value={values.invoiceNumber}
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.InvoiceDetails.Manager}
                      </p>
                      <Select
                        onChange={(option: any) =>
                          setFieldValue('managerId', option)
                        }
                        classNamePrefix="react-select"
                        options={managers}
                        value={values.managerId}
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.InvoiceDetails.InvoiceDate}
                      </p>
                      <Input
                        type="date"
                        name="invoiceDate"
                        onChange={handleChange}
                        value={values.invoiceDate}
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.InvoiceDetails.DueDate}
                      </p>
                      <Input
                        type="date"
                        name="dueDate"
                        onChange={handleChange}
                        value={values.dueDate}
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.InvoiceDetails.JobNumber}
                      </p>
                      <Select
                        onChange={(option: any) =>
                          setFieldValue('invoiceJobId', option)
                        }
                        classNamePrefix="react-select"
                        options={jobNumbers}
                        value={values.invoiceJobId}
                        isClearable
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.invoicesAdd.ContractNumber}
                      </p>
                      <Input
                        type="text"
                        name="contractNumber"
                        onChange={handleChange}
                        value={values.contractNumber}
                        maxLength={20}
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
                        {translation.invoicesAdd.ContractDate}
                      </p>
                      <Input
                        type="date"
                        name="contractDate"
                        onChange={handleChange}
                        value={values.contractDate}
                      />
                    </div>
                    <FinishSendBtn type="submit">
                      {loadingCostUpdate ? (
                        <CircularProgress size={22} />
                      ) : (
                        translation.info.saveButton
                      )}
                    </FinishSendBtn>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          <CostDetailsInfo cost={cost} />
        </div>

        <div className="flex flex-col gap-[24px] px-[16px] pb-[24px] pt-[12px]">
          <div className="flex flex-col gap-[12px]">
            <p className="text-[18px] font-semibold text-[#1D1D1D] md:text-[20px]">
              {translation.InvoiceDetails.ItemList}
            </p>
            <Table
              bordered
              className="custom-bordered"
              scroll={{ x: 'max-content' }}
              dataSource={costLinesDetails?.map((item, index) => ({
                ...item,
                key: index,
              }))}
              columns={itemColumns}
              pagination={false}
              rowClassName={(_, i) =>
                i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
              }
              style={{ tableLayout: 'fixed' }}
            />
          </div>
          <div className="flex flex-col gap-[12px]">
            <p className="text-[18px] font-semibold text-[#1D1D1D] md:text-[20px]">
              {translation.InvoiceDetails.PaymentDetails}
            </p>
            <Table
              bordered
              className="custom-bordered"
              scroll={{ x: 'max-content' }}
              dataSource={costPaymentDetails?.data?.map((item, index) => ({
                ...item,
                key: index,
              }))}
              columns={paymentColumns}
              pagination={false}
              rowClassName={(_, i) =>
                i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
              }
              style={{ tableLayout: 'fixed' }}
            />

            {costPaymentDetails?.data?.length > 0 && (
              <Pagination
                current={pageIndex + 1}
                pageSize={pageSize}
                total={costPaymentDetails && costPaymentDetails.totalRecord}
                onChange={handlePageChange}
                onShowSizeChange={(current, size) => {
                  setPageSize(size);
                  setPageIndex(0);
                }}
                responsive={true}
                style={{ marginTop: '16px', textAlign: 'right' }}
              />
            )}
          </div>
        </div>

        <PaymentModal
          open={openModal}
          handleClose={handleCloseModal}
          id={costId}
          invoiceDetails={costDetails}
          title={translation.PaymentReceived.MakePayment}
          onPaymentAdded={() => {
            queryClient.invalidateQueries([
              REPORTS_EXPENSES_SUMMARY,
              partnerId,
            ]);
          }}
          isCost={true}
        />

        <AddNewInvoiceForm
          open={invoiceDialogState.isOpen}
          handleClose={closeInvoiceDialog}
        />
      </>
    )
  );
};

const FinishSendBtn = styled.button`
  width: 100%;
  height: 48px;
  padding: 0 40px;
  background: #6dcd90;
  border-radius: 6px;
  font-weight: 500;
  color: #fff;
`;

const Select = styled(ReactSelect)`
  & .react-select__value-container {
    padding: 8.7px 16px;
  }
`;

const Input = styled.input`
  background: #ffffff;
  border: 1px solid rgba(41, 45, 50, 0.25);
  width: 100%;
  height: 48px;
  font-family: 'Mark Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  padding: 6px 15px;
  border-radius: 6px;
  :focus {
    outline: none !important;
    border: 2px solid #466afb;
    padding: 5px 14px;
  }
`;
